import { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import CircularProgress from "components/circular-progress";
import { AppointmentsProvider } from "features/scheduling/hooks/use-appointments.hook";
import { useRecordAccessMutation } from "services/patients";
import type { Patient } from "services/types/patients.type";

import { PatientProvider } from "../use-patient.hook";
import { PatientAlertsAccordion } from "./components/patient-alerts-accordion.component";
import { PatientAppointments } from "./components/patient-appointments.component";
import { PatientContactInformation } from "./components/patient-contact-information.component";
import { PatientHeader } from "./components/patient-header.component";
import { PatientHearingAids } from "./components/patient-hearing-aids.component";
import { PatientQuickActions } from "./components/patient-quick-actions.component";
import { PatientRecentOrders } from "./components/patient-recent-orders.component";
import { usePatientDrawer } from "./use-patient-drawer.hook";

type PatientDrawerContentProps = {
  drawerContentRef: React.MutableRefObject<HTMLDivElement | null>;
  isFetching: boolean;
  disableClose: boolean;
  patient: Patient | undefined;
  handleClose: () => void;
};

export default function PatientDrawerContent({
  patient,
  isFetching,
  disableClose,
  drawerContentRef,
  handleClose
}: PatientDrawerContentProps) {
  const [recordAccess] = useRecordAccessMutation();
  const { isCollapsible } = usePatientDrawer();
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (patient && !isFetching && !isCollapsible) {
      recordAccess(patient.id);
    }
  }, [patient, isFetching, isCollapsible, recordAccess]);

  return (
    <>
      {isFetching && <CircularProgress sx={{ width: "100%" }} />}
      {!isFetching && patient && (
        <>
          {!disableClose && (
            <Box
              display="flex"
              justifyContent="end"
              position="sticky"
              width="100%"
              top={0}
              right={0}
            >
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          )}

          <AppointmentsProvider>
            <PatientProvider patientId={String(patient?.id)}>
              <Box
                ref={drawerContentRef}
                sx={{
                  overflowY: "auto",
                  mt: disableClose ? "0" : "-25px",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%"
                }}
              >
                <PatientAlertsAccordion offsetTop={!disableClose} />

                <Box px={2} pt={2.5} pb={0.5}>
                  <PatientHeader patient={patient} />
                </Box>

                <Box px={2} pb={0.5}>
                  <PatientQuickActions patient={patient} />
                </Box>

                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  variant="fullWidth"
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 42,
                    "& .MuiTab-root": { minHeight: 42, fontSize: "18px" }
                  }}
                >
                  <Tab label="Overview" />
                  <Tab label="Devices" />
                  <Tab label="Appointments" />
                </Tabs>

                <Box px={2} py={2} sx={{ overflowY: "auto", flex: 1 }}>
                  {activeTab === 0 && (
                    <PatientContactInformation patient={patient} />
                  )}
                  {activeTab === 1 && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <PatientHearingAids patient={patient} />
                      <PatientRecentOrders patient={patient} />
                    </Box>
                  )}
                  {activeTab === 2 && <PatientAppointments patient={patient} />}
                </Box>
              </Box>
            </PatientProvider>
          </AppointmentsProvider>
        </>
      )}
    </>
  );
}
